import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { Subscription, filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, insuranceOperationsTopic, liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InsuranceApiService, InsuranceProposalListItem } from './insurance-api.service';

@Component({
  standalone: true,
  selector: 'ain-insurance-proposal-desk',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    JalaliDateInputComponent,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    DialogModule,
    FormsModule
  ],
  template: `
    <ain-page
      [title]="t('insurance.proposals.title')"
      [description]="t('insurance.proposals.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.proposals.help.page.title'"
        [descriptionKey]="'insurance.proposals.help.page.description'"
        [bulletKeys]="[
          'insurance.proposals.help.page.bullets.scope',
          'insurance.proposals.help.page.bullets.workflow',
          'insurance.proposals.help.page.bullets.live',
          'insurance.proposals.help.page.bullets.selection'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('insurance.proposals.actions.create')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.bulk')" (click)="applyBulk()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.export')" (click)="exportRows()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <button pButton type="button" [label]="t('insurance.proposals.actions.submitSelected')" [disabled]="!canSubmitSelected()" (click)="submitSelected()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.proposals.actions.cancelSelected')" [disabled]="!canCancelSelected()" (click)="cancelSelected()"></button>
            <button pButton type="button" severity="success" [label]="t('insurance.proposals.actions.bindSelected')" [disabled]="!canBindSelected()" (click)="openBindDialog()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
            </span>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('insurance.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'insurance.proposals.help.listFilters.title'"
                [descriptionKey]="'insurance.proposals.help.listFilters.description'"
                [bulletKeys]="[
                  'insurance.proposals.help.listFilters.bullets.search',
                  'insurance.proposals.help.listFilters.bullets.status',
                  'insurance.proposals.help.listFilters.bullets.selection'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('insurance.proposals.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
              <label>
                <span>{{ t('insurance.proposals.filters.status') }}</span>
                <p-select
                  [options]="proposalStatusOptions"
                  optionValue="value"
                  [(ngModel)]="statusFilter"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
            </div>
          </details>

          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('insurance.proposals.states.empty')" [message]="t('insurance.proposals.states.emptyMessage')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th>{{ t('insurance.proposals.fields.proposalNumber') }}</th>
                  <th>{{ t('insurance.proposals.fields.quoteNumber') }}</th>
                  <th>{{ t('columns.status') }}</th>
                  <th>{{ t('insurance.proposals.fields.boundPolicy') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.proposalNumber }}</button></td>
                  <td><button type="button" class="link-btn" (click)="openQuote(row.quoteId)">{{ row.quoteNumber }}</button></td>
                  <td>{{ t(statusKey(row.status)) }}</td>
                  <td>
                    @if (row.boundPolicyId) {
                      <button type="button" class="link-btn" (click)="openPolicy(row.boundPolicyId)">{{ boundPolicyLabel(row.boundPolicyId) }}</button>
                    } @else {
                      -
                    }
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (info()) { <p class="info">{{ info() }}</p> }
          @if (successMessage()) { <p class="success">{{ successMessage() }}</p> }
          @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
        </section>
      }

      <p-dialog
        [header]="t('insurance.proposalDetail.bindDialog.title')"
        [modal]="true"
        [(visible)]="bindDialogVisible"
        [style]="{ width: '560px' }">
        <div class="dialog-grid">
          <label>
            <span>{{ t('insurance.proposalDetail.fields.effectiveFrom') }}</span>
            <ain-date-input [(ngModel)]="bindEffectiveFromUtc" name="bindEffectiveFromUtc" />
          </label>
          <label>
            <span>{{ t('insurance.proposalDetail.fields.effectiveTo') }}</span>
            <ain-date-input [(ngModel)]="bindEffectiveToUtc" name="bindEffectiveToUtc" />
          </label>
          <label class="wide">
            <span>{{ t('insurance.proposalDetail.fields.idempotencyKey') }}</span>
            <input pInputText [(ngModel)]="bindIdempotencyKey" name="bindIdempotencyKey" />
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="bindDialogVisible.set(false)"></button>
          <button pButton type="button" severity="success" [label]="t('insurance.proposalDetail.actions.bind')" (click)="confirmBindSelected()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .filters { border: 1px solid #e5e7eb; border-radius: 10px; padding: 8px 10px; background: #f9fafb; }
    .filters summary { display: flex; align-items: center; gap: 8px; }
    .filter-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 8px; }
    .filter-grid label { display: grid; gap: 6px; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .dialog-grid .wide { grid-column: 1 / -1; }
    .dialog-grid label { display: grid; gap: 6px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .live-status { margin-inline-start: auto; font-size: 12px; color: #4b5563; }
    .info { color: #1d4ed8; margin: 0; }
    .success { color: #065f46; margin: 0; }
    .error { color: #b91c1c; margin: 0; }
    @media (max-width: 760px) {
      .filter-grid, .dialog-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class InsuranceProposalDeskComponent implements OnInit, OnDestroy {
  private readonly api = inject(InsuranceApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);
  private liveTopic: string | null = null;
  private liveEventsSubscription: Subscription | null = null;
  readonly loading = signal(true);
  readonly rows = signal<InsuranceProposalListItem[]>([]);
  readonly selectedRows = signal<InsuranceProposalListItem[]>([]);
  readonly search = signal('');
  readonly successMessage = signal('');
  readonly errorMessage = signal('');
  readonly info = signal('');
  readonly bindDialogVisible = signal(false);
  readonly policyLabels = signal<Record<string, string>>({});
  statusFilter: string | null = null;
  bindEffectiveFromUtc = new Date().toISOString();
  bindEffectiveToUtc = new Date(Date.now() + (365 * 24 * 60 * 60 * 1000)).toISOString();
  bindIdempotencyKey = crypto.randomUUID();

  readonly proposalStatusOptions = [
    { value: 'Draft', labelKey: 'insurance.proposals.status.draft' },
    { value: 'Submitted', labelKey: 'insurance.proposals.status.submitted' },
    { value: 'Accepted', labelKey: 'insurance.proposals.status.accepted' },
    { value: 'Declined', labelKey: 'insurance.proposals.status.declined' },
    { value: 'Expired', labelKey: 'insurance.proposals.status.expired' },
    { value: 'Bound', labelKey: 'insurance.proposals.status.bound' }
  ];

  ngOnInit(): void {
    this.search.set(this.route.snapshot.queryParamMap.get('q') ?? '');
    this.load();
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    this.liveEventsSubscription?.unsubscribe();
    if (this.liveTopic) {
      void this.liveUpdateService.unsubscribe(this.liveTopic);
      this.liveTopic = null;
    }
  }

  load(): void {
    this.loading.set(true);
    this.info.set('');
    this.successMessage.set('');
    this.errorMessage.set('');
    this.api.listProposals().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.resolvePolicyLabels(rows);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.errorMessage.set(this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  filteredRows(): InsuranceProposalListItem[] {
    const value = this.search().trim().toLowerCase();
    return this.rows()
      .filter(row => !value
        || row.proposalNumber.toLowerCase().includes(value)
        || row.quoteNumber.toLowerCase().includes(value)
        || row.status.toLowerCase().includes(value))
      .filter(row => !this.statusFilter || row.status === this.statusFilter);
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.search().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onSelectionChange(selection: InsuranceProposalListItem[] | InsuranceProposalListItem | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/insurance/proposals', id], {
      queryParams: { q: this.search().trim() || null }
    });
  }

  openQuote(id: string): void {
    void this.router.navigate(['/insurance/quotes', id]);
  }

  openPolicy(id: string): void {
    void this.router.navigate(['/insurance/policies', id]);
  }

  openCreate(): void {
    void this.router.navigate(['/insurance/quotes']);
  }

  applyBulk(): void {
    if (this.selectedRows().length === 0) {
      this.info.set(this.t('insurance.actions.bulkNothingSelected'));
      return;
    }

    this.info.set(this.t('insurance.actions.bulkQueued'));
  }

  exportRows(): void {
    const rows = this.filteredRows();
    if (rows.length === 0) {
      this.info.set(this.t('insurance.actions.exportEmpty'));
      return;
    }

    const header = ['ProposalNumber', 'QuoteNumber', 'Status', 'BoundPolicyId'];
    const lines = rows.map(item =>
      [item.proposalNumber, item.quoteNumber, item.status, item.boundPolicyId ?? '']
        .map(value => `"${value.replace(/"/g, '""')}"`)
        .join(','));
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'insurance-proposals.csv';
    anchor.click();
    URL.revokeObjectURL(url);
    this.info.set(this.t('insurance.actions.exportReady'));
  }

  canSubmitSelected(): boolean {
    return this.selectedRows().length === 1 && this.selectedRows()[0].status === 'Draft';
  }

  canCancelSelected(): boolean {
    if (this.selectedRows().length !== 1) {
      return false;
    }

    const status = this.selectedRows()[0].status;
    return status === 'Draft' || status === 'Submitted' || status === 'Accepted';
  }

  canBindSelected(): boolean {
    if (this.selectedRows().length !== 1) {
      return false;
    }

    const status = this.selectedRows()[0].status;
    return status === 'Submitted' || status === 'Accepted';
  }

  openBindDialog(): void {
    if (!this.canBindSelected()) {
      return;
    }

    this.bindEffectiveFromUtc = new Date().toISOString();
    this.bindEffectiveToUtc = new Date(Date.now() + (365 * 24 * 60 * 60 * 1000)).toISOString();
    this.bindIdempotencyKey = crypto.randomUUID();
    this.bindDialogVisible.set(true);
  }

  submitSelected(): void {
    const row = this.selectedRows()[0];
    if (!row || !confirm(this.t('insurance.proposalDetail.messages.submitConfirm'))) {
      return;
    }

    this.errorMessage.set('');
    this.api.submitProposal(row.id).subscribe({
      next: item => {
        this.upsertRow(item);
        this.successMessage.set(this.t('insurance.proposalDetail.messages.submitted'));
      },
      error: () => this.errorMessage.set(this.t('insurance.states.loadError'))
    });
  }

  cancelSelected(): void {
    const row = this.selectedRows()[0];
    if (!row || !confirm(this.t('insurance.proposalDetail.messages.cancelConfirm'))) {
      return;
    }

    this.errorMessage.set('');
    this.api.cancelProposal(row.id).subscribe({
      next: item => {
        this.upsertRow(item);
        this.successMessage.set(this.t('insurance.proposalDetail.messages.cancelled'));
      },
      error: () => this.errorMessage.set(this.t('insurance.states.loadError'))
    });
  }

  confirmBindSelected(): void {
    const row = this.selectedRows()[0];
    if (!row) {
      return;
    }
    if (!this.bindIdempotencyKey.trim()) {
      this.errorMessage.set(this.t('insurance.proposalDetail.messages.idempotencyRequired'));
      return;
    }

    this.errorMessage.set('');
    this.api.bindProposal(row.id, {
      paymentIntentId: null,
      effectiveFromUtc: this.bindEffectiveFromUtc,
      effectiveToUtc: this.bindEffectiveToUtc
    }, this.bindIdempotencyKey.trim()).subscribe({
      next: item => {
        this.upsertRow(item);
        this.bindDialogVisible.set(false);
        this.bindIdempotencyKey = crypto.randomUUID();
        this.successMessage.set(this.t('insurance.proposalDetail.messages.bound'));
      },
      error: () => this.errorMessage.set(this.t('insurance.states.loadError'))
    });
  }

  private upsertRow(item: InsuranceProposalListItem): void {
    this.rows.update(rows => rows.map(row => row.id === item.id ? { ...row, ...item } : row));
    this.selectedRows.update(rows => rows.map(row => row.id === item.id ? { ...row, ...item } : row));
    if (item.boundPolicyId) {
      this.resolvePolicyLabels([item]);
    }
  }

  boundPolicyLabel(policyId: string): string {
    return this.policyLabels()[policyId] ?? this.t('insurance.proposals.states.policyPending');
  }

  private resolvePolicyLabels(rows: InsuranceProposalListItem[]): void {
    const known = this.policyLabels();
    const pending = [...new Set(
      rows
        .map(row => row.boundPolicyId?.trim())
        .filter((id): id is string => !!id && !known[id])
    )];
    if (pending.length === 0) {
      return;
    }

    this.api.listPolicies().subscribe({
      next: policies => {
        const labels: Record<string, string> = {};
        for (const policy of policies) {
          if (pending.includes(policy.id)) {
            labels[policy.id] = policy.policyNumber;
          }
        }
        if (Object.keys(labels).length > 0) {
          this.policyLabels.update(current => ({ ...current, ...labels }));
        }
      },
      error: () => {}
    });
  }

  statusKey(status: InsuranceProposalListItem['status']): string {
    return `insurance.proposals.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  liveStatusTranslationKey(state: 'disconnected' | 'connecting' | 'connected' | 'reconnecting'): string {
    return liveStatusTranslationKey(state);
  }

  private async startLiveUpdates(): Promise<void> {
    const tenantId = this.currentContext.context()?.tenantId;
    if (!tenantId) {
      return;
    }

    const topic = insuranceOperationsTopic(tenantId);
    this.liveTopic = topic;
    await this.liveUpdateService.subscribe(topic);
    this.liveEventsSubscription = this.liveUpdateService.events$
      .pipe(filter(event => event.eventType === LiveUpdateEventTypes.insuranceQuotesUpdated))
      .subscribe(() => this.load());
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
